/**
 * composeGridEntries — the order and sizing of an opt-in ProductsGrid with
 * content tiles (`tiles`) and/or wide entries (`spans`).
 *
 * Positions are 1-based slots of the FINAL grid (products + tiles), so the
 * same numbers address a tile's `position` and a `spans` key:
 *   - a tile lands on its position, the products flow around it; tiles on one
 *     position keep their order; a position past the end appends the tile;
 *     an invalid position (not an integer ≥ 1) skips the tile with a warning;
 *   - `spans[position]` = desktop columns of whatever sits there (1 or 2,
 *     capped at the desktop column count);
 *   - `fullSm` — the entry fills a whole row of the 2-column mobile grid:
 *     every tile and wide entry, plus the last card of an odd run of cards
 *     between them — or its FIRST card with `oddCard: 'first'` (no orphan
 *     holes; a grid of one card keeps its width).
 *
 * Pure: returns `{ entries, warnings }`, ProductsGrid prints the warnings.
 *
 * @param {object}   args
 * @param {object[]} [args.items]  ProductsGrid items (`id` required)
 * @param {object[]} [args.tiles]  content tiles (`id`, `position`)
 * @param {object}   [args.spans]  `{ [position]: 1 | 2 }`
 * @param {number}   [args.colsSm] mobile (376–767px) column count
 * @param {number}   [args.colsMd] desktop (≥768px) column count
 * @param {'last'|'first'} [args.oddCard] which card of an odd run fills its mobile row (default 'last')
 * @returns {{ entries: {kind: 'product'|'tile', key: string, data: object, position: number, span: number, fullSm: boolean}[], warnings: string[] }}
 */
export const isValidPosition = (position) => Number.isInteger(position) && position >= 1;

const composeGridEntries = ({ items = [], tiles = [], spans = {}, colsSm = 2, colsMd = 3, oddCard = 'last' } = {}) => {
  const warnings = [];

  const queue = tiles
    .filter((tile) => {
      if (isValidPosition(tile.position)) return true;
      warnings.push(`ProductsGrid: tile "${tile.id}" has position ${JSON.stringify(tile.position)} — expected an integer ≥ 1; the tile is skipped.`);
      return false;
    })
    /* Array.prototype.sort is stable, so tiles on one position keep their order */
    .sort((a, b) => a.position - b.position);

  const slots = [];
  let next = 0;
  while (next < items.length || queue.length > 0) {
    const position = slots.length + 1;
    if (queue.length > 0 && (queue[0].position <= position || next >= items.length)) {
      slots.push({ kind: 'tile', data: queue.shift() });
    } else {
      slots.push({ kind: 'product', data: items[next] });
      next += 1;
    }
  }

  Object.keys(spans).forEach((key) => {
    const position = Number(key);
    if (!isValidPosition(position) || position > slots.length) {
      warnings.push(`ProductsGrid: spans has position ${key}, but the grid has no entry there — ignored.`);
    } else if (![1, 2].includes(spans[key])) {
      warnings.push(`ProductsGrid: spans at position ${key} is ${JSON.stringify(spans[key])} — expected 1 or 2; ignored.`);
    }
  });

  const entries = slots.map(({ kind, data }, index) => {
    const position = index + 1;
    const asked = [1, 2].includes(spans[position]) ? spans[position] : 1;
    return {
      kind,
      key: `${kind}-${data.id}`,
      data,
      position,
      span: Math.max(1, Math.min(asked, colsMd)),
      fullSm: false,
    };
  });

  /* Desktop: a wide entry that does not fit what is left of its row wraps and
     leaves a hole. Reported, not reordered — the screen order stays the DOM order. */
  if (colsMd > 1) {
    let column = 0;
    entries.forEach((entry) => {
      if (entry.span > colsMd - column) {
        warnings.push(`ProductsGrid: the entry at position ${entry.position} spans ${entry.span} columns, but its desktop row has ${colsMd - column} left — it wraps and leaves a gap.`);
        column = 0;
      }
      column = (column + entry.span) % colsMd;
    });
  }

  /* Mobile (2 columns): tiles and wide entries fill their row; each run of
     cards between them that ends odd spans its last card (or its first one —
     `oddCard: 'first'` opens the run with a full row instead). */
  if (colsSm === 2 && entries.length > 1) {
    let run = [];
    const closeRun = () => {
      if (run.length % 2 === 1) run[oddCard === 'first' ? 0 : run.length - 1].fullSm = true;
      run = [];
    };
    entries.forEach((entry) => {
      if (entry.kind === 'tile' || entry.span > 1) {
        closeRun();
        entry.fullSm = true;
      } else {
        run.push(entry);
      }
    });
    closeRun();
  }

  return { entries, warnings };
};

export default composeGridEntries;
